'use client';

import React, { useCallback, useEffect, useState } from 'react';
import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import useWalletsToDisplay from 'src/hooks/useWalletsToDisplay';
import {
	DRIFT_CUSTOM_WALLET_OPTIONS,
	DriftWalletName,
} from 'src/constants/wallets';
import {
	getHistoricallySelectedWallets,
	HISTORICALLY_SELECTED_WALLETS_STORAGE_KEY,
} from 'src/hooks/useWalletsToDisplay';
import useHandleSelectWalletProvider from 'src/hooks/useHandleSelectWalletProvider';
import useHasAcknowledgedTerms from 'src/hooks/useHasAcknowledgedTerms';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useCurrentWalletConnectionState from 'src/hooks/useCurrentWalletConnectionState';
import useHasConnectedWalletBefore from 'src/hooks/useHasConnectedWalletBefore';
import MagicAuthLoginPage from './MagicAuthLoginPage';
import { useAccountFetchedAndExists } from 'src/hooks/user/useAccountFetchedAndExists';
import { MainWalletsPage } from './MainWalletsPage';
import { MoreWalletsPage } from './MoreWalletsPage';
import useModal from 'src/components/Modals/hooks/useModal';

const CONNECTION_STEP_LOCK_KEY = 'ONBOARDING_CONNECTING_LOCK';
const MAX_DEFAULT_WALLETS_TO_SHOW = 3;

export interface ConnectWalletModalProps {
	callback?: () => void;
	startPage?: 'main' | 'magic';
}

export default function ConnectWalletModal({
	callback,
	startPage,
}: ConnectWalletModalProps) {
	const walletConnectionState = useCurrentWalletConnectionState();
	const connected = useWalletIsConnected();
	const { show, hide, canGoBack, goBack } = useModal();

	const [hasAcknowledgedTerms] = useHasAcknowledgedTerms();
	const [hasAccountsLoaded, accountExists] = useAccountFetchedAndExists();

	const { handleSelectProvider } = useHandleSelectWalletProvider();
	const [_, setHasConnectedWalletBefore] = useHasConnectedWalletBefore();

	const {
		walletsToShow,
		hiddenWallets,
		hiddenDetectedWallets,
		hiddenNonDetectedWallets,
	} = useWalletsToDisplay(MAX_DEFAULT_WALLETS_TO_SHOW);

	const [isMagicLinkAuth, setIsMagicLinkAuth] = useState(startPage === 'magic');
	const [showMoreWallets, setShowMoreWallets] = useState(false);

	const handleAddToHistoricallySelectedWallets = useCallback((name: string) => {
		const previouslySelectedWallets = getHistoricallySelectedWallets();
		if (!previouslySelectedWallets.includes(name)) {
			previouslySelectedWallets.push(name);
			localStorage.setItem(
				HISTORICALLY_SELECTED_WALLETS_STORAGE_KEY,
				JSON.stringify(previouslySelectedWallets)
			);
		}
	}, []);

	const handleSelectWallet = useCallback(
		async (walletName: DriftWalletName) => {
			handleAddToHistoricallySelectedWallets(walletName);
			switch (walletName) {
				case DRIFT_CUSTOM_WALLET_OPTIONS.MAGIC_AUTH_WALLET_ADAPTER_NAME:
					setIsMagicLinkAuth(true);
					break;
				default: {
					walletConnectionState.instance.update(
						'Connecting',
						true,
						CONNECTION_STEP_LOCK_KEY
					);
					await handleSelectProvider(walletName);
				}
			}
		},
		[
			handleAddToHistoricallySelectedWallets,
			handleSelectProvider,
			walletConnectionState.instance,
		]
	);

	const handleClose = useCallback(
		(callCallback = true) => {
			hide();

			if (callback && callCallback) {
				callback();
			}
		},
		[callback, hide]
	);

	const handleBackFromMagicAuth = () => {
		setIsMagicLinkAuth(false);
	};

	const handleShowMoreWallets = () => {
		setShowMoreWallets(true);
	};

	const handleBackToMainWallets = () => {
		setShowMoreWallets(false);
	};

	// initialize the appkit wallet provider - we dynamically import this so that the appkit wallet provider is only initialized when the modal is opened (reduces initial bundle size)
	useEffect(() => {
		import('src/utils/appKitWalletSetup').then(({ setupAppKitWallet }) =>
			setupAppKitWallet()
		);
	}, []);

	// close modal if user is connected and has already acknowledged terms
	useEffect(() => {
		if (connected && hasAccountsLoaded) {
			setHasConnectedWalletBefore(true);

			if (canGoBack) {
				goBack();
				return;
			}

			if (
				!hasAcknowledgedTerms.shouldShowTermsReminder &&
				accountExists === false
			) {
				// show deposit modal if account doesn't exist
				show({
					type: 'deposit',
					props: {},
				});

				return;
			}

			handleClose();
		}
	}, [
		connected,
		hasAcknowledgedTerms.shouldShowTermsReminder,
		hasAccountsLoaded,
		accountExists,
		handleClose,
		setHasConnectedWalletBefore,
		show,
		goBack,
		canGoBack,
		hide,
	]);

	const title = (() => {
		if (isMagicLinkAuth) {
			return 'Magic Link Login';
		}

		if (showMoreWallets) {
			return 'More Wallets';
		}

		return 'Connect Wallet';
	})();

	const onBack = (() => {
		if (isMagicLinkAuth) {
			return handleBackFromMagicAuth;
		}

		if (showMoreWallets) {
			return handleBackToMainWallets;
		}

		return undefined;
	})();

	const content = (() => {
		if (isMagicLinkAuth) {
			return (
				<MagicAuthLoginPage onBackFromMagicAuth={handleBackFromMagicAuth} />
			);
		}

		if (showMoreWallets) {
			return (
				<MoreWalletsPage
					detectedWallets={hiddenDetectedWallets}
					nonDetectedWallets={hiddenNonDetectedWallets}
					onChooseWallet={handleSelectWallet}
					onBack={handleBackToMainWallets}
				/>
			);
		}

		return (
			<MainWalletsPage
				wallets={walletsToShow}
				hiddenWallets={hiddenWallets}
				onChooseWallet={handleSelectWallet}
				onShowMoreWallets={handleShowMoreWallets}
			/>
		);
	})();

	return (
		<BaseModal.Container
			className="sm:max-h-120"
			onClose={() => handleClose(false)}
			dataTestId="connect-wallet-modal"
		>
			<BaseModal.Title
				onClose={() => handleClose(false)}
				showBack={showMoreWallets || isMagicLinkAuth}
				onBack={onBack}
				closeButtonTestId="connect-wallet-modal-close"
			>
				{title}
			</BaseModal.Title>
			<BaseModal.Body>{content}</BaseModal.Body>
		</BaseModal.Container>
	);
}
